import { Accordion } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { Client } from "#/types/client";

interface Props {
  client: Client;
}

const PLAIN_STEPS = [
  "visits",
  "rhythm",
  "window",
  "load",
  "grouping",
  "route",
  "truck",
  "drivers",
  "quiet",
  "silent",
] as const;

const TECHNICAL_STEPS = [
  "arrivals",
  "visits",
  "gaps",
  "outliers",
  "rhythm",
  "window",
  "late",
  "quiet",
  "quantity",
  "regular",
  "stable",
  "advice",
  "threshold",
  "planningType",
  "groupingRange",
  "groupingDay",
  "groupingLimit",
  "stock",
  "picking",
  "routeOrder",
  "drops",
  "truck",
  "trips",
  "days",
  "schedule",
  "history",
] as const;

/**
 * How the Logistics Prediction works, for the TMA admin. Two blocks: plain
 * words a dispatcher would follow, then the full method with the real names,
 * formulas, sources and a worked example. Values come from the client's
 * settings so the text always matches what runs.
 */
const PredictionHowItWorks = ({ client }: Props) => {
  const { t } = useTranslation();
  const values = {
    gap: client.predictionVisitGapMinutes ?? "—",
    min: client.predictionMinDeliveries ?? 7,
    share: client.predictionRegularSharePercent ?? 50,
    spread: client.predictionMaxSpreadPercent ?? 100,
    radius: client.geofenceRadius ?? 1000,
    dwell: client.prvMinDwellMinutes ?? 120,
    limitHours: Math.round(((client.predictionGroupingLimitMinutes ?? 270) / 60) * 100) / 100,
    offloading: client.predictionOffloadingMinutes ?? 30,
  };

  return (
    <Accordion variant="separated" multiple defaultValue={[]}>
      <Accordion.Item value="plain">
        <Accordion.Control>{t("clients.prediction.howItWorks.plainTitle")}</Accordion.Control>
        <Accordion.Panel>
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-gray-700">
            {PLAIN_STEPS.map((step) => (
              <li key={step}>{t(`clients.prediction.howItWorks.plain.${step}`, values)}</li>
            ))}
          </ol>
        </Accordion.Panel>
      </Accordion.Item>

      <Accordion.Item value="technical">
        <Accordion.Control>{t("clients.prediction.howItWorks.technicalTitle")}</Accordion.Control>
        <Accordion.Panel>
          <dl className="flex flex-col gap-3 text-sm">
            {TECHNICAL_STEPS.map((step) => (
              <div key={step}>
                <dt className="font-semibold text-gray-900">
                  {t(`clients.prediction.howItWorks.technical.${step}.label`)}
                </dt>
                <dd className="text-gray-700">
                  {t(`clients.prediction.howItWorks.technical.${step}.text`, values)}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <span className="font-semibold text-gray-900">
              {t("clients.prediction.howItWorks.example.title")}
            </span>
            <p className="mt-1 whitespace-pre-line">
              {t("clients.prediction.howItWorks.example.text")}
            </p>
          </div>
          <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <span className="font-semibold text-gray-900">
              {t("clients.prediction.howItWorks.routingExample.title")}
            </span>
            <p className="mt-1 whitespace-pre-line">
              {t("clients.prediction.howItWorks.routingExample.text", values)}
            </p>
          </div>
          <p className="mt-3 text-xs text-gray-500">{t("clients.prediction.howItWorks.sources")}</p>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default PredictionHowItWorks;
